/**
 * Time rendering. Europe/Warsaw local on every surface with a UTC toggle;
 * journals stay UTC (D26).
 *
 * The toggle is client-local and persisted in localStorage. It is a display
 * preference, not operator state — the console has no server-side per-user
 * preferences, because read-only means read-only (§8.10).
 */

const KEY = "regrine.tz";
export const LOCAL_TZ = "Europe/Warsaw";

export type TzMode = "local" | "utc";

export function tzMode(): TzMode {
  try {
    return (localStorage.getItem(KEY) as TzMode) ?? "local";
  } catch {
    return "local";
  }
}

/** Notified AFTER the mode has changed.
 *
 *  The bracket control resolves YTD (and a custom range) to instants in the
 *  DISPLAY zone, which means those instants are wrong the moment this
 *  preference changes. Re-rendering is not enough — the resolved `since` is
 *  already computed and would simply be refetched unchanged, so YTD kept the
 *  old zone's new year until the store's 60 s re-tick happened to correct it.
 *  Subscribers re-resolve; `bracket.ts` registers itself at module load. */
const tzListeners = new Set<() => void>();

export function onTzModeChange(fn: () => void): () => void {
  tzListeners.add(fn);
  return () => tzListeners.delete(fn);
}

export function setTzMode(m: TzMode): void {
  try {
    localStorage.setItem(KEY, m);
  } catch {
    /* private mode — the default stands */
  }
  for (const fn of tzListeners) fn();
}

/** The IANA zone the console is DISPLAYING in right now. Every boundary the
 *  bracket control computes (YTD's Jan 1, a custom from/to) is a wall-clock
 *  time in THIS zone, so it has to come from one place. */
export function displayTz(mode: TzMode = tzMode()): string {
  return mode === "utc" ? "UTC" : LOCAL_TZ;
}

function parts(iso: string | null | undefined, mode: TzMode): Intl.DateTimeFormatOptions & { tz: string } {
  void iso;
  return { tz: displayTz(mode) };
}

/** `CET` / `CEST` / `UTC` — the abbreviation in force AT THAT INSTANT.
 *
 *  Which one it is, is a fact about the instant, not about the zone: the
 *  console used to print the constant string "CEST/CET" beside every
 *  timestamp, which names both and commits to neither. A reader checking
 *  whether YTD started on the right side of the DST change needs the one
 *  that actually applied. */
export function zone(iso: string | number | Date | null | undefined, mode: TzMode = tzMode()): string {
  const d = iso === null || iso === undefined ? new Date() : new Date(iso as string);
  if (Number.isNaN(d.getTime())) return mode === "utc" ? "UTC" : LOCAL_TZ;
  const p = new Intl.DateTimeFormat("en-GB", { timeZone: displayTz(mode), timeZoneName: "short" })
    .formatToParts(d)
    .find((x) => x.type === "timeZoneName");
  return p?.value ?? (mode === "utc" ? "UTC" : LOCAL_TZ);
}

/** Milliseconds to ADD to a UTC instant to read it as wall clock in `tz`. */
function zoneOffsetMs(utcMs: number, tz: string): number {
  const f = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const m: Record<string, number> = {};
  for (const p of f.formatToParts(new Date(utcMs))) if (p.type !== "literal") m[p.type] = Number(p.value);
  // `hour12: false` reports midnight as 24 in some ICU builds.
  const wall = Date.UTC(m.year!, m.month! - 1, m.day!, m.hour! % 24, m.minute!, m.second!);
  return wall - utcMs;
}

/**
 * A wall-clock reading in the DISPLAY zone → the UTC instant it names.
 *
 * Deliberately not `new Date(y, 0, 1)`: that builds the instant in the
 * BROWSER's zone, which is whatever laptop the operator opened the console
 * on and has nothing to do with the zone the console is displaying. A reader
 * in Singapore flipping the toggle to LOCAL must get Warsaw's Jan 1.
 *
 * Two probes, not one: the offset is sampled at the naive guess and then
 * re-sampled at the corrected instant, which is what makes a boundary that
 * lands within a DST shift resolve to the same instant the formatter would
 * print back.
 */
export function zonedWallToUtc(
  y: number,
  month1: number,
  day: number,
  hour = 0,
  minute = 0,
  second = 0,
  mode: TzMode = tzMode(),
): Date {
  const tz = displayTz(mode);
  const naive = Date.UTC(y, month1 - 1, day, hour, minute, second);
  let ms = naive - zoneOffsetMs(naive, tz);
  ms = naive - zoneOffsetMs(ms, tz);
  return new Date(ms);
}

/** The calendar year `at` falls in, read in the DISPLAY zone. On 1 Jan at
 *  00:30 CET this is 2027 while UTC still says 2026 — which is the whole
 *  point of YTD being a display-timezone question. */
export function zonedYear(at: Date = new Date(), mode: TzMode = tzMode()): number {
  return Number(new Intl.DateTimeFormat("en-US", { timeZone: displayTz(mode), year: "numeric" }).format(at));
}

/** `2026-05-01T00:00` — the value shape `<input type="datetime-local">` speaks,
 *  in the DISPLAY zone rather than the browser's. */
export function toLocalInput(iso: string | null | undefined, mode: TzMode = tzMode()): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const f = new Intl.DateTimeFormat("en-CA", {
    timeZone: displayTz(mode),
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
  const m: Record<string, string> = {};
  for (const p of f.formatToParts(d)) if (p.type !== "literal") m[p.type] = p.value;
  const hh = String(Number(m.hour) % 24).padStart(2, "0");
  return `${m.year}-${m.month}-${m.day}T${hh}:${m.minute}`;
}

/** The inverse. A malformed value returns null — never "now", never epoch. */
export function fromLocalInput(v: string | null | undefined, mode: TzMode = tzMode()): string | null {
  if (!v) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(v.trim());
  if (!m) return null;
  const d = zonedWallToUtc(+m[1]!, +m[2]!, +m[3]!, +m[4]!, +m[5]!, m[6] ? +m[6] : 0, mode);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** `2026-05-01 00:00` in the display zone — the form the scope line uses. */
export function shortStamp(iso: string | null | undefined, mode: TzMode = tzMode()): string {
  const v = toLocalInput(iso, mode);
  return v ? v.replace("T", " ") : "—";
}

/** `19:42:07` — the feed-row form. Never a bare Date.toString(). */
export function clock(iso: string | null | undefined, mode: TzMode = tzMode()): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "?";
  return new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZone: parts(iso, mode).tz,
  }).format(d);
}

/** `2026-07-26 19:42:07` — the case-header form. */
export function stamp(iso: string | null | undefined, mode: TzMode = tzMode()): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "?";
  const f = new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZone: parts(iso, mode).tz,
  }).format(d);
  return `${f} ${zone(iso, mode)}`;
}

/** `3h04m` — an AGE, always with its unit, never "3" alone. */
export function age(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return "—";
  const s = Math.max(0, Math.floor(seconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h${String(m % 60).padStart(2, "0")}m`;
  return `${Math.floor(h / 24)}d`;
}

export function ageSince(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "?";
  return age((now - t) / 1000);
}
